import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Component, ErrorInfo, ReactNode } from "react";

/**
 * 1NOW-1642 — the app previously had no error boundary anywhere, so a single
 * render-time throw (a null field on one vehicle record, say) unmounted the
 * whole tree and left the renter staring at a blank tenant site.
 *
 * Wrap the router once, and each major section separately, so a bad record
 * degrades that section instead of taking the page down with it.
 */
interface Props {
  children: ReactNode;
  /** Shown in place of the failed subtree. Omit for the default notice. */
  fallback?: ReactNode;
  /** Identifies the failing area in the console — e.g. "listing-grid". */
  name?: string;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Kept as console.error rather than a toast: renters can't action this,
    // and the surrounding page still works. PostHog is already loaded app-wide
    // if we later want these reported.
    console.error(
      `[ErrorBoundary${this.props.name ? `: ${this.props.name}` : ""}]`,
      error,
      errorInfo.componentStack,
    );
  }

  /**
   * Re-mount the failed subtree. Worth offering before a full reload: the
   * common cause is one bad record in a list that a refetch resolves, and a
   * reload costs the renter their place in the booking flow. If it throws
   * again the boundary simply catches again.
   */
  private handleRetry = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    return (
      <div className="container py-16">
        <div className="mx-auto max-w-md rounded-2xl border border-neutral-200 bg-neutral-50 px-6 py-8 text-center">
          <span
            aria-hidden="true"
            className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(190, 18, 60, 0.08)" }}
          >
            <ExclamationTriangleIcon
              className="h-6 w-6"
              style={{ color: "#be123c" }}
            />
          </span>
          <p className="text-text-color font-semibold">Something went wrong</p>
          <p className="text-text-grey text-sm mt-2">
            This section couldn't be loaded. The rest of the page still works.
          </p>
          <button
            type="button"
            onClick={this.handleRetry}
            className="mt-5 inline-flex items-center justify-center rounded-lg border border-neutral-200 px-4 py-2 text-sm font-semibold text-text-color transition-colors hover:bg-neutral-100"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
